import React, { useState } from 'react';
import { handleTrack } from '../../../../../../../utils/tracker';
import { Button } from '../../../../../../Atoms/Button';
import { EditIcon } from '../../../../../../Icons/Edit/edit';
import { addQueryParamToURL } from '../../../../../../Utils/CommonUtils';
import { VIEW_TYPE } from '../../data';
import { LEAD_DETAILS, PLAN_DATA, QuestionAnswer } from '../../types';
import { BasicForm } from '../basic-form';
import { KeyFeatures } from './KeyFeatures';
import { PlanCard } from './PlanCard';
import { QnASidebar } from './Sidebar';

interface PLAN_VIEW {
  planData: PLAN_DATA[];
  handleBack: () => void;
  changeView: (viewType: VIEW_TYPE) => void;
  selectedAnswers: Record<string, string[]>;
  leadDetails: LEAD_DETAILS;
  sideBarData: QuestionAnswer;
}

export const PlanView = ({
  planData,
  handleBack,
  changeView,
  selectedAnswers,
  leadDetails,
  sideBarData,
}: PLAN_VIEW): JSX.Element => {
  const [isFormVisible, setIsFormVisible] = useState(false);

  if (!planData || !planData?.length) return <></>;

  const handleButtonClick = (
    e: React.MouseEvent | React.TouchEvent,
    viewType: VIEW_TYPE
  ) => {
    handleTrack({ label: 'qna_modal_button', obj: e.target });
    viewType === VIEW_TYPE.chat ? changeView(viewType) : handleBack();
  };

  const DIY_PLAN_DATA = planData[0];
  const EXPERT_PLAN_DATA = planData[1];

  return (
    <div
      className="flex divide-x md:flex-col-reverse sm:flex-col-reverse"
      data-testid="plan-view-section"
    >
      <div className="w-4/12 flex flex-col justify-between px-9 py-16 sm:w-full md:w-full md:p-6 sm:p-4">
        <div>
          <QnASidebar sidebarData={sideBarData} />
          <div
            onClick={(e) => handleButtonClick(e, VIEW_TYPE.questionAnswer)}
            className="text-s-20 text-font-200 cursor-pointer mb-9"
          >
            <div
              data-label="You can change recommendation"
              className="flex items-center justify-start gap-1 text-blue-350 text-s-20 font-semibold sm:text-s-14"
            >
              <EditIcon /> Edit Details
            </div>
          </div>
        </div>
        <div>
          <img
            src="https://assets.clear.in/cleartax/images/1709806654_image29.png"
            alt=""
            width={29}
            height={31}
          />
          <div className="text-font-500 font-bold text-base mb-4 mt-7">
            Having trouble
          </div>
          <div className="text-font-200 text-base mb-6">
            Feel free to contact us and we will always help you through the
            process.
          </div>

          {isFormVisible ? (
            <BasicForm
              changeView={changeView}
              selectedAnswers={selectedAnswers}
              leadDetails={leadDetails}
            />
          ) : (
            <Button
              onClick={() => setIsFormVisible(true)}
              className="bg-transparent border border-font-500 text-font-500 sm:w-full"
            >
              Contact us
            </Button>
          )}
        </div>
      </div>
      <div className="w-8/12 px-12 py-16 md:w-full sm:w-full sm:p-4 sm:pt-8">
        <div className="text-s-32 mb-9 sm:text-s-24 font-semibold sm:hidden">
          We have the perfect plan for you!
        </div>
        <div
          className="flex items-start gap-12 mb-9 sm:flex-col-reverse md:gap-9 sm:gap-9"
          data-testid="price-cards"
        >
          <div className="w-5/12 lg:w-1/2 md:w-1/2 sm:w-full">
            <PlanCard
              title="File Yourself"
              desc="Do your own taxes"
              icon="https://assets.clear.in/cleartax/images/1709835095_frame18142193201.svg"
              buttonText="Start for Free"
              bgImage="https://assets.clear.in/cleartax/images/1709833796_maskgroup17.png"
              bgColor="#F4F7FA"
              className="border border-font-500 bg-transparent text-font-500"
              isAssistedFiling={false}
              planData={DIY_PLAN_DATA}
              showTopStrip={false}
            />
          </div>
          <div className="w-5/12 lg:w-1/2 md:w-1/2 sm:w-full">
            <PlanCard
              title="Hire an Expert"
              desc="Experts help as you do your taxes"
              icon="https://assets.clear.in/cleartax/images/1709835106_frame18142193202.svg"
              buttonText="Buy now"
              bgImage="https://assets.clear.in/cleartax/images/1709833956_maskgroup18.png"
              bgColor="#FFFAE2"
              className="bg-blue-350 text-white"
              isAssistedFiling={true}
              planData={EXPERT_PLAN_DATA}
              showTopStrip={true}
            />
          </div>
        </div>

        <KeyFeatures data={EXPERT_PLAN_DATA?.marketplacePlan.features} />

        <div className="mt-7 text-center text-s-14 sm:text-s-12">
          Looking for something else?{' '}
          <a
            className="text-s-14 sm:text-s-12"
            href={addQueryParamToURL(
              window.location.href,
              'planId',
              'our_recommendation'
            )}
          >
            Explore other plans
          </a>
        </div>
      </div>
    </div>
  );
};
